import { useApolloClient } from "@apollo/client";
import { assertNever, getDataOrNull } from "@msys/common";
import { DataGrid, GridColDef, ModalOpenButton } from "@msys/ui";
import { Button, Link } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { namedOperations } from "../../../../clients/graphqlTypes.js";
import {
  BreadcrumbItem,
  Page,
  PageTopbarItem,
} from "../../../commons/layout/Page.js";
import { PageContainer } from "../../../commons/layout/PageContainer.js";
import { DebugAllPropsButton } from "../../../features/doc-items/debug/PropertiesDebug.js";
import { AttributeExpressionModal } from "../../../features/doc-items/modals/AttributeExpressionModal.js";
import { ItemProductSearchFilterExpressionModal } from "../../../features/doc-items/modals/ItemProductSearchFilterExpressionModal.js";
import { PropertyExpressionModal } from "../../../features/doc-items/modals/PropertyExpressionModal.js";
import { PropertyMappingExpressionModal } from "../../../features/templates/quote/PropertyMappingExpressionModal.js";
import { buildDocPath } from "../../../utils.js";
import {
  TemplateQuoteEditTroubleshoot_CompileDocIsolatedExpressionResultDiagnosticFragment,
  useTemplateQuoteEditTroubleshootQuery,
} from "./TemplateQuoteEditTroubleshoot.generated.js";

interface Props {
  docId: string;
  submenuItems: PageTopbarItem[];
  prefixBreadcrumbs: BreadcrumbItem[];
  pathToDocList: string;
  pageSubHeader: React.ReactElement | null;
}

export function TemplateQuoteEditTroubleshoot({
  docId,
  submenuItems,
  prefixBreadcrumbs,
  pathToDocList,
  pageSubHeader,
}: Props) {
  const { t } = useTranslate(["Global", "Templates", "Expressions"]);

  const client = useApolloClient();
  const query = useTemplateQuoteEditTroubleshootQuery({
    client,
    variables: { id: docId },
    notifyOnNetworkStatusChange: true,
  });
  const quoteTemplate = getDataOrNull(
    query.data?.quoteTemplateLatest
  )?.quoteTemplate;
  const expressionValidationResults = getDataOrNull(
    query.data?.compileDocIsolatedExpression
  )?.results;

  const columns: GridColDef<TemplateQuoteEditTroubleshoot_CompileDocIsolatedExpressionResultDiagnosticFragment>[] =
    [
      {
        field: "title",
        headerName: t("Item", {
          ns: "Global",
        }),
        flex: 1,
        renderCell: ({ row }) => (
          <Link
            href={`${buildDocPath(
              `${pathToDocList}/${row.docId}`,
              row.versionNumber ?? null
            )}${row.versionNumber ? "" : "/edit"}/items/${row.itemId}`}
            target="_blank"
            onClick={event => {
              event.stopPropagation();
            }}
          >{`${row.itemPdfPath} ${row.itemTitle}`}</Link>
        ),
      },
      {
        field: "kind",
        headerName: t("Kind", {
          ns: "Expressions",
        }),
        flex: 2,
        valueGetter: (value, row) => {
          if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticComputedProp"
          ) {
            return t("Property", {
              ns: "Expressions",
            });
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticAttribute"
          ) {
            return t("Attribute", {
              ns: "Expressions",
            });
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticPropertyMapping"
          ) {
            return t("Property mapping", {
              ns: "Expressions",
            });
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticRecommendedEligibleIf"
          ) {
            return t("Item recommendation eligible if", {
              ns: "Expressions",
            });
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticRecommendedIncludeIf"
          ) {
            return t("Item recommendation include if", {
              ns: "Expressions",
            });
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticProductSearchFilterExpression"
          ) {
            return t("Product search filter expression", {
              ns: "Expressions",
            });
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticAskIf"
          ) {
            return "AskIf";
          } else {
            assertNever(row);
          }
        },
      },
      {
        field: "location",
        headerName: t("Location", {
          ns: "Expressions",
        }),
        flex: 1,
        valueGetter: (value, row) => {
          if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticComputedProp"
          ) {
            return row.key;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticAttribute"
          ) {
            return row.attribute;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticPropertyMapping"
          ) {
            return row.toKey;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticRecommendedEligibleIf"
          ) {
            return row.itemRecommendationRank;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticRecommendedIncludeIf"
          ) {
            return row.itemRecommendationRank;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticProductSearchFilterExpression"
          ) {
            return row.propertyKey;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticAskIf"
          ) {
            return row.askIfExpr;
          } else {
            assertNever(row);
          }
        },
      },
      {
        field: "expression",
        headerName: t("Formula", {
          ns: "Global",
        }),
        flex: 2,
        renderCell: ({ row }) => {
          if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticComputedProp"
          ) {
            return (
              <ModalOpenButton
                Modal={PropertyExpressionModal}
                modalProps={{
                  projectId: null,
                  docId: row.docId,
                  itemId: row.itemId,
                  fieldName: row.key,
                  expression: row.expr,
                }}
              >
                <Link>{row.expr}</Link>
              </ModalOpenButton>
            );
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticAttribute"
          ) {
            return (
              <ModalOpenButton
                Modal={AttributeExpressionModal}
                modalProps={{
                  projectId: null,
                  docId: row.docId,
                  itemId: row.itemId,
                  fieldName: row.attribute,
                  expression: row.expr,
                }}
              >
                <Link>{row.expr}</Link>
              </ModalOpenButton>
            );
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticRecommendedEligibleIf"
          ) {
            return row.expr;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticRecommendedIncludeIf"
          ) {
            return row.expr;
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticPropertyMapping"
          ) {
            return (
              <ModalOpenButton
                Modal={PropertyMappingExpressionModal}
                modalProps={{
                  projectId: null,
                  docId: row.docId,
                  itemId: row.itemId,
                  fieldName: row.toKey,
                  expression: row.fromExpr,
                }}
              >
                <Link>{row.fromExpr}</Link>
              </ModalOpenButton>
            );
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticProductSearchFilterExpression"
          ) {
            return (
              <ModalOpenButton
                Modal={ItemProductSearchFilterExpressionModal}
                modalProps={{
                  projectId: null,
                  docId: row.docId,
                  itemId: row.itemId,
                  filterKind: row.filterKind,
                  filterLabel: row.filterKey,
                  propertyKey: row.propertyKey ?? undefined,
                  expression: row.expr,
                  operator: row.operator,
                  refetchQueries: [
                    namedOperations.Query.TemplateQuoteEditTroubleshoot,
                  ],
                }}
              >
                <Link>{row.expr}</Link>
              </ModalOpenButton>
            );
          } else if (
            row.__typename ===
            "CompileDocIsolatedExpressionResultDiagnosticAskIf"
          ) {
            return null;
          } else {
            assertNever(row);
          }
        },
      },
      {
        field: "messageText",
        headerName: t("Error", {
          ns: "Global",
        }),
        flex: 3,
        valueGetter: (value, row) => row.messageText,
      },
    ];

  return (
    <Page
      title={quoteTemplate?.title ?? ""}
      subtitle={t("Document Templates", { ns: "Templates" })}
      submenuItems={submenuItems}
      breadcrumbs={prefixBreadcrumbs}
      subHeader={pageSubHeader}
      headerType="template"
      action={
        quoteTemplate && [
          <DebugAllPropsButton
            key="debug-all-props"
            projectId={null}
            docId={docId}
          />,
          <Button
            key="rerun-checks"
            onClick={() => {
              query.refetch();
            }}
            loading={query.loading}
            variant="contained"
          >
            {t("Re-run checks", {
              ns: "Global",
            })}
          </Button>,
        ]
      }
    >
      <PageContainer>
        <DataGrid<TemplateQuoteEditTroubleshoot_CompileDocIsolatedExpressionResultDiagnosticFragment>
          columns={columns}
          rows={expressionValidationResults ?? []}
          getRowId={row =>
            `${row.itemId}-${row.__typename}-${JSON.stringify(row)}`
          }
          paginationMode="client"
          loading={query.loading}
        />
      </PageContainer>
    </Page>
  );
}
